import { ArrowRight, CalendarClock } from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Pill } from "@/components/ui/pill";
import { Button } from "@/components/ui/button";
import { formatDisplay } from "@/lib/datetime-format";
import { ConfidenceChip } from "@/features/simulations/ConfidenceChip";
import type { AppliedScenario } from "@/types/applied-scenario.types";
import {
  PHASE_LABELS,
  PHASE_TONES,
  VERDICT_LABELS,
  VERDICT_TONES,
  WINDOW_BASIS_LABELS,
} from "./applied-scenario-labels";
import { FrozenScopeList } from "./FrozenScopeList";

interface AppliedScenarioCardProps {
  scenario: AppliedScenario;
  onCancel: (scenario: AppliedScenario) => void;
  onSettle: (scenario: AppliedScenario) => void;
  isSettling: boolean;
  expanded: boolean;
  onToggleExpand: () => void;
  /** Apuração e interferência entram por aqui — o card não sabe montá-las. */
  children?: React.ReactNode;
}

// Um COMPROMISSO na lista. A fase vem do servidor derivada do relógio; o card não a
// recalcula (recalcular na borda faria a tela e o servidor discordarem no minuto da virada).

export function AppliedScenarioCard({
  scenario,
  onCancel,
  onSettle,
  isSettling,
  expanded,
  onToggleExpand,
  children,
}: AppliedScenarioCardProps) {
  const isActive = scenario.state === "ACTIVE";
  const canSettle = scenario.phase === "settleable";

  return (
    <Card>
      <CardHeader className="flex flex-col gap-2 pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="flex flex-wrap items-center gap-2">
            {/* Status com cor + dot + rótulo — nunca só por cor. */}
            <Pill tone={PHASE_TONES[scenario.phase]} withDot>
              {PHASE_LABELS[scenario.phase]}
            </Pill>
            {/* O veredito de aderência AQUI, na lista, para que o gestor
                identifique o compromisso em risco sem abrir nenhum. O valor vem PRONTO do servidor
                (última virada, ou o congelado se apurado): derivá-lo no cliente faria a tela e o
                servidor discordarem, e recalculá-lo por item custaria quatro leituras por card. */}
            {scenario.adherence ? (
              <Pill tone={VERDICT_TONES[scenario.adherence.verdict]} withDot>
                {VERDICT_LABELS[scenario.adherence.verdict]}
                {scenario.adherence.index !== null
                  ? ` · ${scenario.adherence.index.toFixed(1).replace(".", ",")}%`
                  : " · —"}
              </Pill>
            ) : null}
          </span>
          <span className="flex items-center gap-1.5 text-[13px] tabular-nums text-text-secondary">
            {formatDisplay(scenario.windowStart)}
            <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
            {formatDisplay(scenario.windowEnd)}
          </span>
        </div>
        <p className="text-[15px] font-medium text-text-primary">{scenario.reason}</p>
        <p className="flex items-start gap-1.5 text-[12px] text-text-secondary">
          <CalendarClock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {WINDOW_BASIS_LABELS[scenario.windowBasis]} · {scenario.plannedMinutes} min de expediente
          comprometidos
        </p>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[12px] text-text-secondary">
          <span className="flex items-center gap-1.5">
            Cenário escolhido{" "}
            <ConfidenceChip
              level={scenario.winnerConfidence}
              basis={scenario.winnerConfidenceBasis}
              trackRecordSource={scenario.winnerTrackRecordSource}
            />
          </span>
          <span className="flex items-center gap-1.5">
            Não fazer nada{" "}
            <ConfidenceChip
              level={scenario.baselineConfidence}
              basis={scenario.baselineConfidenceBasis}
              trackRecordSource={scenario.baselineTrackRecordSource}
            />
          </span>
          <span>
            Por {scenario.appliedByName ?? "—"} · motor {scenario.engineVersion}
          </span>
        </div>

        <FrozenScopeList orders={scenario.orders} />

        {scenario.state === "CANCELLED" && scenario.cancelReason ? (
          <p className="text-[12px] text-text-secondary">
            Cancelado: <span className="text-text-primary">{scenario.cancelReason}</span>
          </p>
        ) : null}
        {scenario.state === "SUPERSEDED" && scenario.supersededAt ? (
          <p className="text-[12px] text-text-secondary">
            Substituído por outro compromisso em {formatDisplay(scenario.supersededAt)}.
          </p>
        ) : null}
        {scenario.phase === "awaiting_settlement" && scenario.settlementAvailableAt ? (
          <p className="text-[12px] text-text-secondary">
            A apuração libera em {formatDisplay(scenario.settlementAvailableAt)} — a carência existe
            para que a produção do período termine de ser lançada.
          </p>
        ) : null}

        {expanded ? children : null}

        <div className="flex flex-wrap justify-end gap-2">
          {/* A apuração é consultada sob demanda: abrir todas de uma vez varreria a produção da
              janela de cada compromisso da linha, e a maioria nunca é aberta. */}
          {scenario.state === "SETTLED" ? (
            <Button variant="ghost" onClick={onToggleExpand}>
              {expanded ? "Ocultar apuração" : "Ver apuração"}
            </Button>
          ) : (
            // Em vigência o que há para abrir é a ADERÊNCIA, e ela é recalculada pelo
            // servidor ao abrir. Mesma disciplina: caro sob demanda, barato na lista.
            <Button variant="ghost" onClick={onToggleExpand}>
              {expanded ? "Ocultar aderência" : "Ver aderência"}
            </Button>
          )}
          {isActive ? (
            <>
              <Button variant="outline" onClick={() => onCancel(scenario)}>
                Cancelar compromisso
              </Button>
              <Button disabled={!canSettle || isSettling} onClick={() => onSettle(scenario)}>
                {isSettling ? "Apurando…" : "Apurar resultado"}
              </Button>
            </>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
